import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { MOBILE_LAYOUT_QUERIES } from '../../constants/responsive-breakpoints';
import { dirtyOrTouchedErrorStateMatcher } from '../../constants/dirty-or-touched-error-state-matcher';
import { validationMessages } from '../../messages';
import { UiSelectOptions } from '../../models/ui-select-option.model';
import { SELECT_ALL_VALUE } from '../../constants/select-all-value';

@Component({
  selector: 'app-ui-select',
  templateUrl: './ui-select.component.html',
  styleUrls: ['./ui-select.component.scss'],
  standalone: false,
  host: {
    '[class.ui-form-field--no-hint-row]': 'isCompact',
  },
})
export class SharedUiSelectComponent implements OnChanges, OnDestroy {
  private readonly isMobileViewport = toSignal(
    inject(BreakpointObserver)
      .observe(MOBILE_LAYOUT_QUERIES)
      .pipe(map((result) => result.matches)),
    { initialValue: MOBILE_LAYOUT_QUERIES.some((query) => window.matchMedia(query).matches) }
  );

  readonly errorStateMatcher = dirtyOrTouchedErrorStateMatcher;

  @Input() label = '';
  @Input() description = '';
  @Input() control: FormControl;
  @Input() options: UiSelectOptions = [];
  @Input() messages = validationMessages;
  @Input() placeholder?: string;
  @Input() selected: string[] | string | any = [];
  @Input() isMultiple = true;
  @Input() disabled = false;
  @Input() required = false;
  @Input() sortByName = false;
  @Input() enableBlankOption = false;
  @Input() returnData = false;
  @Input() returnWholeObject = false;
  @Input() enableSearch = true;
  @Input() selectAll = false;
  @Input() selectAllSingleChoice = false;
  @Input() panelClass = '';
  @Input() triggerLabel = '';
  @Input() emptyTriggerLabel = '';
  @Input() triggerPrefix = '';
  @Input() compact = false;
  @Input() compactOnMobile = false;
  @Input() hasIndent = false;
  @Input() indent = 8;
  @Input() searchMode: 'local' | 'api' = 'local';
  @Input() showSearchApiHint = true;
  @Input() hasMore = false;
  @Input() searchPredicate?: (option: any, searchTerm: string) => boolean;

  @Output() readonly searchChange = new EventEmitter<string>();
  @Output() readonly loadMore = new EventEmitter<void>();
  @Output() readonly optionClick = new EventEmitter<string | number>();

  sortedOptions: UiSelectOptions = [];
  areSomeRowsSelected = false;
  areAllRowsSelected = false;
  selectedOptionNameKeys: string[] = [];
  selectedOptionName = '';
  selectedOptionPaused = false;
  SELECT_ALL_VALUE = SELECT_ALL_VALUE;

  @Output() readonly changeHandler: EventEmitter<MatSelectChange> =
    new EventEmitter<MatSelectChange>();

  @ViewChild('searchInput') searchInput: ElementRef;

  private initialOptions: UiSelectOptions;
  private selectedOptionForApi: NonNullable<UiSelectOptions>[number] | null = null;
  private controlSubscription?: Subscription;
  private disabledAppliedByInput = false;
  private scrollHandler: (() => void) | null = null;
  private scrollContainer: HTMLElement | null = null;
  private lastLoadMoreEmit = 0;
  private readonly loadMoreThrottleMs = 500;

  get isCompact(): boolean {
    return this.compact || (this.compactOnMobile && this.isMobileViewport());
  }

  ngOnDestroy(): void {
    this.controlSubscription?.unsubscribe();
    this.removeScrollListener();
  }

  setSelectAllCheckboxProperties(): void {
    const opts = this.getDisplayedOptions();
    if (opts && Array.isArray(this.control.value)) {
      this.areSomeRowsSelected =
        this.control.value.length > 0 && this.control.value.length < opts.length;
      this.areAllRowsSelected = opts.length > 0 && this.control.value.length === opts.length;
    } else {
      this.areAllRowsSelected = false;
      this.areSomeRowsSelected = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.control) {
      this.releasePreviousControl(changes.control.previousValue as FormControl);
    }
    if (changes.disabled || changes.control) {
      this.syncDisabledInput();
    }
    if (changes.options?.currentValue !== changes.options?.previousValue) {
      if (this.searchMode !== 'api') {
        this.initialOptions = this.options;
      }
      if (this.searchMode === 'api' && this.control?.value && this.options?.length) {
        const selectedId = this.getSelectedOptionId(
          Array.isArray(this.control.value) ? this.control.value[0] : this.control.value
        );
        const found = this.options.find((o) => o.id === selectedId);
        if (found) {
          this.selectedOptionForApi = found;
        }
      }
      if (this.searchInput?.nativeElement && this.searchMode !== 'api') {
        this.searchInput.nativeElement.value = '';
      }
    }
    if (changes.isRequired?.currentValue === true) {
      this.control.setValidators(Validators.required);
      this.control.updateValueAndValidity();
    } else if (changes.isRequired?.currentValue === false) {
      this.control.clearValidators();
      this.control.updateValueAndValidity();
    }
    if (changes?.options?.currentValue?.length > 0) {
      this.sortedOptions = [...changes.options.currentValue].sort((a, b) =>
        a.name > b.name ? 1 : -1
      );
    }

    if (this.selectAll) {
      this.setSelectAllCheckboxProperties();
    }

    this.refreshTriggerLabels();
    if (changes.control) {
      this.bindControl();
    }
  }

  onPanelOpenedChange(opened: boolean): void {
    if (opened) {
      this.focusInput();
      this.addScrollListener();
    } else {
      this.clearSearchOnClose();
      this.removeScrollListener();
    }
  }

  private clearSearchOnClose(): void {
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
    if (this.searchMode === 'api') {
      this.searchChange.emit('');
      return;
    }
    this.options = this.initialOptions ?? this.options;
  }

  focusInput(): void {
    if (!this.initialOptions && this.options) {
      this.initialOptions = this.options;
    }
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.focus();
    }
    if (this.searchMode === 'api') {
      return;
    }
    this.options = this.initialOptions ?? this.options;
  }

  private scrollPanelToTop(): void {
    const panel = document.querySelector('.mat-mdc-select-panel.app-ui-select');
    if (panel) {
      (panel as HTMLElement).scrollTop = 0;
    }
  }

  private addScrollListener(): void {
    this.removeScrollListener();
    setTimeout(() => {
      const panel = document.querySelector('.mat-mdc-select-panel.app-ui-select');
      if (!panel || this.scrollHandler) {
        return;
      }
      this.scrollContainer = panel as HTMLElement;
      this.scrollContainer.scrollTop = 0;
      this.scrollHandler = () => this.onPanelScroll();
      this.scrollContainer.addEventListener('scroll', this.scrollHandler);
      setTimeout(() => this.scrollPanelToTop(), 50);
    });
  }

  private removeScrollListener(): void {
    if (this.scrollContainer && this.scrollHandler) {
      this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
      this.scrollContainer = null;
      this.scrollHandler = null;
    }
  }

  private onPanelScroll(): void {
    if (
      this.searchMode !== 'api' ||
      !this.hasMore ||
      !this.scrollContainer ||
      !this.searchInput?.nativeElement
    ) {
      return;
    }
    const searchValue = (this.searchInput.nativeElement.value ?? '').trim();
    if (searchValue !== '') {
      return;
    }
    const { scrollTop, clientHeight, scrollHeight } = this.scrollContainer;
    const threshold = 50;
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      const now = Date.now();
      if (now - this.lastLoadMoreEmit < this.loadMoreThrottleMs) {
        return;
      }
      this.lastLoadMoreEmit = now;
      this.loadMore.emit();
    }
  }

  // Receive user input and send to search method**
  onSearch(event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;
    const value = target.value ?? '';
    const trimmedValue = value.trim();

    if (
      event.code === 'Space' ||
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'Space'
    ) {
      // prevent closing the dropdown on space press
      event.preventDefault();
      event.stopPropagation();
      target.value += ' ';
    }

    if (this.searchMode === 'api') {
      if (trimmedValue.length === 0) {
        this.searchChange.emit('');
        return;
      }

      if (trimmedValue.length >= 3) {
        this.searchChange.emit(trimmedValue);
      }
    } else {
      this.options = value.length > 0 ? this.search(value) : this.initialOptions;
    }
  }

  // Filter the states list and send back to populate the selectedStates**
  search(value: string) {
    const filter = value.toLowerCase();
    return this.initialOptions?.filter((option: any) => {
      if (this.searchPredicate) {
        return this.searchPredicate(option, value);
      }
      return option.name.toLowerCase().includes(filter);
    });
  }

  onChange(event: MatSelectChange): void {
    if (this.searchMode === 'api' && event.value != null && !this.isMultiple) {
      const selectedId = this.getSelectedOptionId(event.value);
      const selected = this.getDisplayedOptions()?.find((o) => o.id === selectedId);
      if (selected) {
        this.selectedOptionForApi = selected;
      }
      this.searchChange.emit('');
    }
    this.changeHandler.emit(event);
  }

  getDisplayedOptions(): UiSelectOptions {
    const opts = this.options ?? [];

    if (this.searchMode === 'api') {
      return this.pinSelectedApiOptions(opts);
    }

    // Local mode: mat-select hides selection when selected options aren't in the DOM.
    // Always include selected options in the displayed list, even when they don't match the search.
    const fullOptions = this.initialOptions ?? this.options ?? [];
    const selectedValues = Array.isArray(this.control?.value) ? this.control.value : [];
    const selectedIds = selectedValues
      .map((v) => (this.returnWholeObject ? v?.id : v))
      .filter((id) => id != null && id !== this.SELECT_ALL_VALUE);

    if (selectedIds.length === 0) {
      const singleId = this.returnWholeObject ? this.control?.value?.id : this.control?.value;
      if (singleId != null && singleId !== this.SELECT_ALL_VALUE) {
        const selectedOption = fullOptions.find((o) => o.id === singleId);
        if (selectedOption && opts.every((o) => o.id !== singleId)) {
          return [selectedOption, ...opts];
        }
      }
      return opts;
    }

    const filteredIds = new Set(opts.map((o) => o.id));
    const selectedNotInFiltered = selectedIds
      .filter((id) => !filteredIds.has(id))
      .map((id) => fullOptions.find((o) => o.id === id))
      .filter(Boolean) as NonNullable<UiSelectOptions>[number][];
    if (selectedNotInFiltered.length > 0) {
      return [...selectedNotInFiltered, ...opts];
    }
    return opts;
  }

  getDisplayedOptionsForRender(): UiSelectOptions {
    const opts = this.getDisplayedOptions();
    if (!opts?.length) {
      return opts ?? [];
    }
    if (this.sortByName) {
      return [...opts].sort((a, b) => (a.name > b.name ? 1 : -1));
    }
    return opts;
  }

  compareWith(o1: any, o2: any): boolean {
    return o1?.id && o2?.id ? o1.id === o2.id : Object.is(o1, o2);
  }

  toggleSelection(change: any): void {
    if (change.checked) {
      const allOptions = this.getDisplayedOptions()?.map((option) => option.id);
      this.control.setValue(allOptions as any[]);
    } else {
      this.control.setValue([]);
    }

    this.setSelectAllCheckboxProperties();
  }

  private syncDisabledInput(): void {
    if (!this.control) {
      return;
    }
    if (this.disabled && this.control.enabled) {
      this.control.disable({ emitEvent: false });
      this.disabledAppliedByInput = true;
      return;
    }
    if (this.disabled) {
      return;
    }
    if (this.disabledAppliedByInput && this.control.disabled && !this.control.parent?.disabled) {
      this.control.enable({ emitEvent: false });
    }
    this.disabledAppliedByInput = false;
  }

  private releasePreviousControl(previous: FormControl | undefined): void {
    if (this.disabledAppliedByInput && previous?.disabled && !previous.parent?.disabled) {
      previous.enable({ emitEvent: false });
    }
    this.disabledAppliedByInput = false;
  }

  private refreshTriggerLabels(): void {
    const value = this.control?.value;
    if (this.isMultiple) {
      this.selectedOptionNameKeys = this.getMultipleSelectedOptionNameKeys(
        Array.isArray(value) ? value : []
      );
      this.selectedOptionName = '';
      this.selectedOptionPaused = false;
      return;
    }

    this.selectedOptionNameKeys = [];
    this.selectedOptionName = this.getSelectedOptionName(value);
    this.selectedOptionPaused = this.isSelectedOptionPaused(value);
  }

  isSelectedOptionPaused(value: unknown): boolean {
    return !!this.findSelectedOption(value)?.isPaused;
  }

  getSelectedOptionName(value: any): string {
    if (value === null || value === undefined) return '';

    const optionId = this.getSelectedOptionId(value);

    if (optionId === SELECT_ALL_VALUE) {
      return 'all';
    }

    const option = this.findSelectedOption(value);
    return option?.name || '';
  }

  getMultipleSelectedOptionNames(values: any[]): string {
    return this.getMultipleSelectedOptionNameKeys(values).join(', ');
  }

  getMultipleSelectedOptionNameKeys(values: any[]): string[] {
    if (!values || !Array.isArray(values) || values.length === 0) return [];

    return values.map((value) => this.getSelectedOptionName(value)).filter((name) => name !== '');
  }

  private findSelectedOption(value: unknown): NonNullable<UiSelectOptions>[number] | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    const optionsToSearch = this.getDisplayedOptions() ?? [];
    if (this.returnData) {
      return optionsToSearch.find((option) => this.isSelectedData(option.data, value));
    }

    const optionId = this.getSelectedOptionId(value);
    const selectedOption = optionsToSearch.find((option) => option.id === optionId);
    return selectedOption ?? this.getFallbackWholeObjectOption(value);
  }

  private getSelectedOptionId(value: unknown): unknown {
    if (this.returnWholeObject || this.isObjectWithId(value)) {
      return (value as { id?: unknown })?.id;
    }

    return value;
  }

  private isSelectedData(optionData: unknown, value: unknown): boolean {
    if (Object.is(optionData, value)) {
      return true;
    }

    return this.hasMatchingId(optionData, value);
  }

  private getFallbackWholeObjectOption(
    value: unknown
  ): NonNullable<UiSelectOptions>[number] | undefined {
    if (!this.returnWholeObject || !this.isObjectWithId(value)) {
      return undefined;
    }

    return value as NonNullable<UiSelectOptions>[number];
  }

  private isObjectWithId(value: unknown): value is { id?: unknown } {
    return typeof value === 'object' && value !== null && 'id' in value;
  }

  private hasMatchingId(left: unknown, right: unknown): boolean {
    if (!this.isObjectWithId(left) || !this.isObjectWithId(right)) {
      return false;
    }

    return left.id !== null && left.id !== undefined && left.id === right.id;
  }

  private pinSelectedApiOptions(opts: UiSelectOptions): UiSelectOptions {
    const selected = this.getApiSelectedOptions();
    if (!selected.length) {
      return opts;
    }
    const optionIds = new Set((opts ?? []).map((option) => option.id));
    const missing = selected.filter((option) => option.id != null && !optionIds.has(option.id));
    return missing.length ? [...missing, ...(opts ?? [])] : opts;
  }

  private getApiSelectedOptions(): NonNullable<UiSelectOptions>[number][] {
    const value = this.control?.value;
    if (Array.isArray(value)) {
      return value
        .map((item) => this.resolveApiSelectedOption(item))
        .filter((option): option is NonNullable<UiSelectOptions>[number] => !!option);
    }
    const fromValue = this.resolveApiSelectedOption(value);
    if (fromValue) {
      return [fromValue];
    }
    return this.selectedOptionForApi ? [this.selectedOptionForApi] : [];
  }

  private resolveApiSelectedOption(
    value: unknown
  ): NonNullable<UiSelectOptions>[number] | undefined {
    const optionId = this.getSelectedOptionId(value);
    if (optionId == null || optionId === this.SELECT_ALL_VALUE) {
      return undefined;
    }
    if (this.selectedOptionForApi?.id === optionId) {
      return this.selectedOptionForApi;
    }
    return this.getFallbackWholeObjectOption(value);
  }

  private bindControl(): void {
    this.controlSubscription?.unsubscribe();
    if (!this.initialOptions && this.options) {
      this.initialOptions = this.options;
    }
    this.controlSubscription = this.control.valueChanges.subscribe((value) => {
      this.refreshTriggerLabels();
      if (this.selectAll) {
        this.setSelectAllCheckboxProperties();
      }
      if (this.searchMode === 'api' && (value == null || value === '')) {
        this.selectedOptionForApi = null;
      }
    });
  }
}
